import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { FileText, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useClinicalEncounter } from '@/hooks/useConsultasClinicas';
import { usePaciente } from '@/hooks/usePaciente';
import { api } from '@/lib/api';
import { formatarData } from '@/lib/utils';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/pep-vida/_authed/consultas/$id/historico')({
  component: HistoricoConsultaPage,
});

type GeneratedDocument = { id: string; type: string; title: string; content: string; createdAt?: string };

function HistoricoConsultaPage() {
  const { id } = Route.useParams();
  const { data: encounter, isLoading, error, refetch } = useClinicalEncounter(id);
  const { data: paciente } = usePaciente(encounter?.cdPaciente ?? null);
  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);

  useEffect(() => {
    if (!encounter) return;
    api.get<GeneratedDocument[]>(`/api/generated-documents/encounter/${encounter.id}`).then(setDocuments).catch(() => setDocuments([]));
  }, [encounter?.id]);

  if (isLoading) return <LoadingState />;
  if (error) {
    if (error.message.includes('não encontrada')) throw notFound();
    return <ErrorState message={error.message} onRetry={() => refetch()} />;
  }
  if (!encounter) throw notFound();

  const soap = encounter.soapNote;
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">Histórico PEP</div>
          <h1 className="text-xl font-semibold">Atendimento realizado</h1>
        </div>
        <Badge variant="secondary" className="capitalize">{encounter.status.replace('_', ' ')}</Badge>
      </div>

      {paciente && (
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
              {paciente.ds_paciente.split(' ').slice(0, 2).map((part) => part[0]).join('')}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-primary">Paciente</div>
              <h2 className="mt-1 text-xl font-semibold">{paciente.ds_paciente}</h2>
              <div className="mt-2 text-sm text-muted-foreground">{paciente.dt_nascimento} · CPF {paciente.nr_cpf ?? '-'}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Dados do atendimento</CardTitle></CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-3">
          <div><span className="text-muted-foreground">Data de início</span><div className="font-medium">{formatarData(encounter.startedAt)}</div></div>
          <div><span className="text-muted-foreground">Atendimento Clinux</span><div className="font-medium">{encounter.cdAtendimento}</div></div>
          <div><span className="text-muted-foreground">Médico</span><div className="font-medium">Consulta PEP</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Evolução SOAP</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {[
            ['Subjetivo', soap?.subjetivo],
            ['Objetivo', soap?.objetivo],
            ['Avaliação', soap?.avaliacao],
            ['Plano', soap?.plano],
          ].map(([label, value]) => (
            <div key={label} className="rounded-md border p-4">
              <div className="mb-2 text-sm font-semibold">{label}</div>
              <p className="min-h-16 whitespace-pre-wrap text-sm text-muted-foreground">{value || 'Não informado.'}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Documentos gerados neste atendimento</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {documents.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum documento gerado.</p> : documents.map((document) => (
            <div key={document.id} className="flex items-center gap-3 rounded-md border p-3">
              <FileText className="h-4 w-4 text-primary" />
              <div><div className="font-medium">{document.title}</div><div className="text-xs text-muted-foreground">{document.type}</div></div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-start">
        <Link to="/pep-vida/consultas/$id" params={{ id }}>
          <Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Voltar ao atendimento</Button>
        </Link>
      </div>
    </div>
  );
}
